import React, { useState, useEffect } from 'react';
import Modal from '@shared/ui/Modal';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import {
  Copy,
  Check,
  ExternalLink,
  Mail,
  Loader2,
  QrCode,
  Download,
  Code,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import Button from '../Button';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface ShareModalAction {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  shareUrl?: string;
  /** Fetch the public URL from the outreach items API when shareUrl is not provided */
  itemId?: string;
  /** Custom async function to resolve the share URL when shareUrl and itemId aren't suitable */
  resolveUrl?: () => Promise<string | null>;
  /** Customize the text shared on social platforms */
  shareText?: string;
  /** Custom email subject for mailto sharing */
  emailSubject?: string;
  /** Custom email body for mailto sharing */
  emailBody?: string;
  /** Show a "Preview" action button */
  onPreview?: () => void;
  /** Show an "Email Distribution" action button */
  onEmailDistribution?: () => void;
  /** Additional custom actions rendered after the built-in share options */
  customActions?: ShareModalAction[];
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  title,
  shareUrl: shareUrlProp,
  itemId,
  resolveUrl,
  shareText,
  emailSubject,
  emailBody,
  onPreview,
  onEmailDistribution,
  customActions = []
}) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [generatingQr, setGeneratingQr] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  const [showTrackingUrl, setShowTrackingUrl] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);

  const publicUrl = shareUrlProp || resolvedUrl;

  useEffect(() => {
    if (!isOpen) return;
    setQrDataUrl(null);
    setCopied(false);
    setEmbedCopied(false);
    setShowTrackingUrl(false);
    setShowEmbed(false);

    if (!shareUrlProp && resolveUrl) {
      resolveUrlFromCallback();
    } else if (!shareUrlProp && itemId) {
      fetchPublicUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId, shareUrlProp, resolveUrl]);

  const resolveUrlFromCallback = async () => {
    try {
      setLoading(true);
      const url = await resolveUrl!();
      setResolvedUrl(url);
    } catch (error) {
      console.error('Failed to resolve URL:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicUrl = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/outreach-unified/items/${itemId}`);
      const collectors = response.data.data?.collectors || [];
      const active = collectors.find((c: any) => c.is_active);
      if (active) {
        setResolvedUrl(`${window.location.origin}/o/${active.slug}`);
      }
    } catch (error) {
      console.error('Failed to fetch public URL:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyEmbed = async () => {
    if (!publicUrl) return;
    try {
      const snippet = `<iframe src="${publicUrl}" width="100%" height="600" frameborder="0" style="border:none;"></iframe>`;
      await navigator.clipboard.writeText(snippet);
      setEmbedCopied(true);
      toast.success('Embed code copied!');
      setTimeout(() => setEmbedCopied(false), 2000);
    } catch {
      toast.error('Failed to copy embed code');
    }
  };

  const handleGenerateQr = async () => {
    if (!publicUrl) return;
    setGeneratingQr(true);
    try {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(publicUrl)}`;
      setQrDataUrl(qrUrl);
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrDataUrl) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${title.replace(/\s+/g, '_')}_qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyQr = async () => {
    if (!qrDataUrl) return;
    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      setQrCopied(true);
      toast.success('QR code copied to clipboard!');
      setTimeout(() => setQrCopied(false), 2000);
    } catch {
      toast.error('Failed to copy QR code');
    }
  };

  const handleEmailShare = () => {
    if (!publicUrl) return;
    const subject = emailSubject || title;
    const body = emailBody || `Check out: ${publicUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const a = document.createElement('a');
    a.href = mailtoUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSocialShare = (platform: 'facebook' | 'twitter' | 'linkedin' | 'bluesky') => {
    if (!publicUrl) return;
    const text = shareText || title;
    const urls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(publicUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`,
      bluesky: `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${publicUrl}`)}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (!isOpen) return null;

  const trackingUrl = publicUrl
    ? `${publicUrl}${publicUrl.includes('?') ? '&' : '?'}utm_source=share&utm_medium=manual`
    : '';

  return (
    <Modal
      isOpen={true}
      title="Share"
      description={title}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
        <div className="space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : !publicUrl ? (
            <div className="text-center py-8 text-text-secondary dark:text-text-dark-secondary">
              No shareable link available. Please launch the item first.
            </div>
          ) : (
            <>
              {/* Preview / Email Distribution Actions */}
              {(onPreview || onEmailDistribution) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {onPreview && (
                    <button
                      onClick={onPreview}
                      className="flex items-center gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                    >
                      <Eye className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Preview</div>
                        <div className="text-xs text-text-secondary dark:text-text-dark-secondary">See how it looks</div>
                      </div>
                    </button>
                  )}
                  {onEmailDistribution && (
                    <button
                      onClick={onEmailDistribution}
                      className="flex items-center gap-3 p-3 border border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">Email Lists</div>
                        <div className="text-xs text-text-secondary dark:text-text-dark-secondary">Send to contacts</div>
                      </div>
                    </button>
                  )}
                </div>
              )}

              {/* Copy Link Section */}
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={publicUrl}
                    readOnly
                    className="input flex-1 bg-background-tertiary dark:bg-background-dark-tertiary text-sm"
                  />
                  <Button onClick={handleCopy} variant="secondary" size="sm">
                    {copied ? <Check className="w-4 h-4 text-success-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    onClick={() => window.open(publicUrl, '_blank')}
                    variant="secondary"
                    size="sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>

                {/* Full Tracking URL */}
                <button
                  onClick={() => setShowTrackingUrl(!showTrackingUrl)}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mt-2"
                >
                  {showTrackingUrl ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  <span>View full tracking URL</span>
                </button>
                {showTrackingUrl && (
                  <div className="mt-1.5 p-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded border border-border-light dark:border-border-dark">
                    <p className="text-xs text-text-secondary dark:text-text-dark-secondary font-mono break-all">
                      {trackingUrl}
                    </p>
                  </div>
                )}
              </div>

              {/* QR Code Section */}
              <div className="border border-border-light dark:border-border-dark rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                    QR Code
                  </span>
                  {!qrDataUrl && (
                    <Button
                      onClick={handleGenerateQr}
                      variant="secondary"
                      size="sm"
                      disabled={generatingQr}
                    >
                      {generatingQr ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-1" />
                          Generate
                        </>
                      )}
                    </Button>
                  )}
                </div>
                {qrDataUrl && (
                  <div className="flex flex-col items-center gap-3 mt-3">
                    <img
                      src={qrDataUrl}
                      alt="QR Code"
                      className="w-40 h-40 border border-border-light dark:border-border-dark rounded"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleCopyQr} variant="secondary" size="sm">
                        {qrCopied ? <Check className="w-4 h-4 mr-1 text-success-500" /> : <Copy className="w-4 h-4 mr-1" />}
                        {qrCopied ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button onClick={handleDownloadQr} variant="secondary" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Share Via */}
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Share via
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  <button
                    onClick={handleEmailShare}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  >
                    <Mail className="w-5 h-5 text-text-secondary dark:text-text-dark-secondary" />
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Email</span>
                  </button>
                  <button
                    onClick={() => handleSocialShare('facebook')}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  >
                    {/* eslint-disable-next-line no-restricted-syntax -- Facebook brand colour; a third-party mark, not a design token */}
                    <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Facebook</span>
                  </button>
                  <button
                    onClick={() => handleSocialShare('twitter')}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  >
                    <svg className="w-5 h-5 text-text-primary dark:text-text-dark-primary" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">X</span>
                  </button>
                  <button
                    onClick={() => handleSocialShare('linkedin')}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  >
                    {/* eslint-disable-next-line no-restricted-syntax -- LinkedIn brand colour; a third-party mark, not a design token */}
                    <svg className="w-5 h-5 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleSocialShare('bluesky')}
                    className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  >
                    {/* eslint-disable-next-line no-restricted-syntax -- Bluesky brand colour; a third-party mark, not a design token */}
                    <svg className="w-5 h-5 text-[#0085FF]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
                    </svg>
                    <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Bluesky</span>
                  </button>
                </div>
              </div>

              {/* Custom Actions */}
              {customActions.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {customActions.map((action) => (
                    <button
                      key={action.id}
                      onClick={action.onClick}
                      className="flex items-start gap-3 p-3 border border-border-light dark:border-border-dark rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors text-left"
                    >
                      {action.icon}
                      <div>
                        <div className="text-sm font-medium text-text-primary dark:text-text-dark-primary">{action.label}</div>
                        <div className="text-xs text-text-secondary dark:text-text-dark-secondary">{action.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Embed Code Section */}
              <div className="border border-border-light dark:border-border-dark rounded-lg p-3">
                <button
                  onClick={() => setShowEmbed(!showEmbed)}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary" />
                    <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                      Embed on your website
                    </span>
                  </div>
                  {showEmbed ? (
                    <ChevronUp className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                  )}
                </button>
                {showEmbed && (
                  <div className="mt-3 space-y-2">
                    <div className="p-2 bg-background-tertiary dark:bg-background-dark-tertiary rounded border border-border-light dark:border-border-dark">
                      <code className="text-xs text-text-secondary dark:text-text-dark-secondary break-all">
                        {`<iframe src="${publicUrl}" width="100%" height="600" frameborder="0" style="border:none;"></iframe>`}
                      </code>
                    </div>
                    <Button onClick={handleCopyEmbed} variant="secondary" size="sm">
                      {embedCopied ? <Check className="w-4 h-4 mr-1 text-success-500" /> : <Copy className="w-4 h-4 mr-1" />}
                      {embedCopied ? 'Copied!' : 'Copy embed code'}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

    </Modal>
  );
};

export default ShareModal;
